import classNames from 'classnames';
import { UIEvent, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { RiArrowDownSFill, RiArrowRightSFill } from 'react-icons/ri';
import { generatePath, useNavigate } from 'react-router-dom';

import { BuildingReadModel, useGetSpacesQuery } from '@client/api';
import {
  Chip,
  combineStrings,
  IconButton,
  isDefinedNumber,
  isDefinedString,
  Pagination,
  Table,
  TableBody,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  Text,
  toISODateStringOrUndefined,
  toNumberString,
} from '@client/toolkit';

import { BuildingKeyValueBox } from './building-key-value-box.component';

import { SpaceRow } from '../../../components/space-row.component';
import { PortfolioPaths } from '../../../portfolio.router';
import { useBuildingsFilterQueryParams, useBuildingsSearchQueryParams } from '../hooks/use-buildings-query-params';

type BuildingItemProps =
  | {
      building: BuildingReadModel;
      isFetching?: false;
    }
  | {
      building?: BuildingReadModel;
      isFetching: true;
    };

export function BuildingItem({ building, isFetching }: BuildingItemProps) {
  const { t, ready } = useTranslation('rentalObjects');
  const { t: tGlobal } = useTranslation('global');
  const { t: tType } = useTranslation('type');

  const [filterQueryParams] = useBuildingsFilterQueryParams();
  const [searchQueryParams] = useBuildingsSearchQueryParams();

  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const hasSpaces = useMemo(() => !!building?.rentalObjects?.total?.count, [building]);

  const pageSize = 20;
  const [page, setPage] = useState(0);
  const pageOffset = page * pageSize;

  const { data: { items: spaces, totalCount } = { items: [], totalCount: 0 }, isFetching: isFetchingSpace } =
    useGetSpacesQuery(
      {
        'ancestorPath.Equal': building?.path,
        'pagination.Limit': pageSize,
        'pagination.Offset': pageOffset,
        'discriminator.Contains': ['ResidentialUnit', 'CommercialSpace'],
        'sort.Property': 'Name',
        'sort.Order': 'Asc',
        'available.Equal.Start': filterQueryParams.availableDateEnabled
          ? toISODateStringOrUndefined(filterQueryParams.availableDateFrom)
          : undefined,
        'available.Equal.End': filterQueryParams.availableDateEnabled
          ? toISODateStringOrUndefined(filterQueryParams.availableDateTo)
          : undefined,
        'vacant.Equal.Start': filterQueryParams.vacantDateEnabled
          ? toISODateStringOrUndefined(filterQueryParams.vacantDateFrom)
          : undefined,
        'vacant.Equal.End': filterQueryParams.vacantDateEnabled
          ? toISODateStringOrUndefined(filterQueryParams.vacantDateTo)
          : undefined,
        'bedroomCount.From':
          filterQueryParams.bedroomCountEnabled && filterQueryParams.bedroomCountFrom
            ? filterQueryParams.bedroomCountFrom
            : undefined,
        'bedroomCount.To':
          filterQueryParams.bedroomCountEnabled && filterQueryParams.bedroomCountTo
            ? filterQueryParams.bedroomCountTo
            : undefined,
        'bathroomCount.From':
          filterQueryParams.bathroomCountEnabled && filterQueryParams.bathroomCountFrom
            ? filterQueryParams.bathroomCountFrom
            : undefined,
        'bathroomCount.To':
          filterQueryParams.bathroomCountEnabled && filterQueryParams.bathroomCountTo
            ? filterQueryParams.bathroomCountTo
            : undefined,
        search: searchQueryParams.buildingsSearch ? searchQueryParams.buildingsSearch : undefined,
      },
      { skip: !building?.id || !hasSpaces || !open },
    );

  const buildingType = useMemo(() => building?.type, [building?.type]);
  const buildingTypeLabel = useMemo(
    () => (buildingType ? tType(`BuildingType.${buildingType}`) : undefined),
    [buildingType, tType],
  );

  const totalArea = useMemo(
    () => building?.commercialSpaces.total?.area.metric,
    [building?.commercialSpaces.total?.area.metric],
  );
  const rentedArea = useMemo(
    () => building?.commercialSpaces.rented?.area.metric,
    [building?.commercialSpaces.rented?.area.metric],
  );
  const vacantArea = useMemo(
    () => building?.commercialSpaces.vacant?.area.metric,
    [building?.commercialSpaces.vacant?.area.metric],
  );
  const commonArea = useMemo(() => building?.commonAreaTotal?.area.metric, [building?.commonAreaTotal?.area.metric]);

  const rentedCount = useMemo(() => building?.rentalObjects.rented?.count, [building?.rentalObjects.rented?.count]);
  const vacantCount = useMemo(() => building?.rentalObjects.vacant?.count, [building?.rentalObjects.vacant?.count]);

  const buildingStreet = useMemo(() => building?.address?.streetAddress1, [building?.address?.streetAddress1]);
  const buildingCity = useMemo(() => building?.address?.municipality, [building?.address?.municipality]);

  const buildingAddress = useMemo(
    () => combineStrings(' / ', buildingCity, buildingStreet),
    [buildingCity, buildingStreet],
  );

  const handleSelectBuilding = useCallback(
    (building?: BuildingReadModel) => {
      if (!building?.id) return;

      navigate(generatePath(PortfolioPaths.Building, { buildingId: building.id }));
    },
    [navigate],
  );

  const toggleOpen = useCallback(() => setOpen((currentOpen) => !currentOpen), []);
  const onArrowClick = useCallback(
    (event: UIEvent) => {
      toggleOpen();
      event.stopPropagation();
    },
    [toggleOpen],
  );

  return (
    <div className="border-t border-border">
      <div
        onClick={() => handleSelectBuilding(building)}
        className={classNames(
          'relative flex flex-col items-stretch px-2 py-2',
          !isFetching ? 'cursor-pointer hover:bg-surface-hover transition ease-in-out' : undefined,
          `before:content-['_']`,
          'before:block before:absolute before:left-0 before:top-0 before:bottom-0 before:max-h-[3rem] before:h-[calc(100%-8px)] before:w-1 before:my-auto before:rounded-r-lg before:pointer-events-none',
          'before:transition-color before:duration-100 before:ease-in-out',
          'before:dark:bg-transparent before:bg-transparent',
        )}
      >
        <div className="flex items-center justify-start">
          <IconButton
            color="primary"
            size="sm"
            fontSize="md"
            className={classNames('mr-2', !hasSpaces && 'invisible opacity-0')}
            onClick={onArrowClick}
          >
            {open ? <RiArrowDownSFill /> : <RiArrowRightSFill />}
            <span className="sr-only">Show Spaces</span>
          </IconButton>

          <div className="relative overflow-hidden flex-1 min-w-0">
            <div className="flex min-w-0 gap-4">
              <Text
                as="div"
                className="text-sm leading-tight text-white font-bold whitespace-nowrap"
                loading={isFetching}
                isEmpty={!isDefinedString(building?.name)}
                title={isDefinedString(building?.name) ? building?.name : undefined}
              >
                {building?.name}
              </Text>
              <Chip
                fontWeight="none"
                color="none"
                title={buildingTypeLabel}
                className={classNames(
                  'mr-2 text-[0.65rem] px-2 py-1 leading-none font-medium whitespace-nowrap bg-[#353130] text-white flex items-center',
                )}
              >
                <Text as="span" loading={isFetching}>
                  {buildingTypeLabel}
                </Text>
              </Chip>
            </div>
            <div className="flex items-center overflow-hidden">
              <FaMapMarkerAlt className="text-xs text-red-600 inline mr-1" />
              <Text
                as="div"
                className="text-sm font-normal text-secondary whitespace-nowrap"
                loading={isFetching}
                isEmpty={!isDefinedString(buildingAddress)}
                title={isDefinedString(buildingAddress) ? buildingAddress : undefined}
              >
                {buildingAddress}
              </Text>
            </div>
            {isDefinedString(building?.buildingNumber) && (
              <Text
                as="div"
                className="text-xs font-normal text-secondary whitespace-nowrap"
                loading={isFetching}
              >
                {tGlobal('building.buildingNumber')} {building?.buildingNumber}
              </Text>
            )}
          </div>
          <div className="shrink-0 flex items-center">
            <div className="flex items-center gap-4">
              {buildingType && (
                <>
                  <div className="px-3 py-2 text-left w-[160px]">
                    {isFetching || !building?.tags?.length ? (
                      <Text
                        as="div"
                        className="text-xs font-bold text-white whitespace-nowrap"
                        loading={isFetching}
                        isEmpty
                      />
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {building.tags.map((tag) => (
                          <Chip
                            key={tag}
                            fontWeight="none"
                            color="none"
                            title={tag}
                            className="text-[0.65rem] px-2 py-1 leading-none font-medium whitespace-nowrap bg-[#353130] text-white"
                          >
                            {tag}
                          </Chip>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="px-3 py-2 text-left w-[90px]">
                    <Text
                      as="div"
                      className="text-xs font-bold text-white whitespace-nowrap"
                      loading={isFetching}
                      isEmpty={!isDefinedNumber(totalArea)}
                    >
                      {toNumberString(totalArea ?? undefined, undefined, {
                        unit: tType('AreaMeasurementUnit.SquareMeters', { context: 'short' }),
                      })}
                    </Text>
                  </div>
                  <div className="px-3 py-2 text-left w-[155px]">
                    <Text
                      as="div"
                      className="text-xs font-bold text-white whitespace-nowrap"
                      loading={isFetching}
                      isEmpty={!isDefinedNumber(rentedArea)}
                    >
                      {toNumberString(rentedArea ?? undefined, undefined, {
                        unit: tType('AreaMeasurementUnit.SquareMeters', { context: 'short' }),
                      })}
                    </Text>
                  </div>
                  <div className="px-3 py-2 text-left w-[90px]">
                    <Text
                      as="div"
                      className="text-xs font-bold text-white whitespace-nowrap"
                      loading={isFetching}
                      isEmpty={!isDefinedNumber(vacantArea)}
                    >
                      {toNumberString(vacantArea ?? undefined, undefined, {
                        unit: tType('AreaMeasurementUnit.SquareMeters', { context: 'short' }),
                      })}
                    </Text>
                  </div>
                  <div className="px-3 py-2 text-left w-[90px]">
                    <Text
                      as="div"
                      className="text-xs font-bold text-white whitespace-nowrap"
                      loading={isFetching}
                      isEmpty={!isDefinedNumber(commonArea)}
                    >
                      {toNumberString(commonArea ?? undefined, undefined, {
                        unit: tType('AreaMeasurementUnit.SquareMeters', { context: 'short' }),
                      })}
                    </Text>
                  </div>
                </>
              )}
              <div className="px-3 py-2 text-left min-w-[80px]">
                <Text
                  as="div"
                  className="text-xs font-bold text-white whitespace-nowrap"
                  loading={isFetching}
                  isEmpty={!isDefinedNumber(rentedCount)}
                >
                  {rentedCount}
                </Text>
              </div>
              <div className="px-3 py-2 text-left min-w-[80px]">
                <Text
                  as="div"
                  className="text-xs font-bold text-white whitespace-nowrap"
                  loading={isFetching}
                  isEmpty={!isDefinedNumber(vacantCount)}
                >
                  {vacantCount}
                </Text>
              </div>
            </div>
          </div>
        </div>
      </div>

      {open && (
        <>
          <Table className="w-full sm:table-auto md:table-fixed">
            <TableHead className="bg-stone-700">
              <TableHeadRow>
                <TableHeadCell>{tGlobal('space.areaName')}</TableHeadCell>
                <TableHeadCell>{tGlobal('building.type')}</TableHeadCell>
                <TableHeadCell>{tGlobal('building.assetClass')}</TableHeadCell>
                <TableHeadCell>{tGlobal('space.area')}</TableHeadCell>
                <TableHeadCell>{tGlobal('entity.tenant')}</TableHeadCell>
                <TableHeadCell>{tGlobal('space.status')}</TableHeadCell>
                {/* <TableHeadCell>{tGlobal('space.rent')}</TableHeadCell> */}
              </TableHeadRow>
            </TableHead>
            <TableBody>
              {isFetchingSpace && Array.from(Array(2).keys()).map((idx) => <SpaceRow key={idx} />)}
              {!isFetchingSpace && spaces.map((space) => <SpaceRow key={space.id} space={space} />)}
            </TableBody>
          </Table>
          <Pagination
            className="border-t"
            totalCount={totalCount}
            currentPage={page}
            setPage={setPage}
            pageSize={pageSize}
          />
        </>
      )}
    </div>
  );
}
